import React from 'react'
import { Controller, useFormContext } from 'react-hook-form'
import { useIntl } from 'react-intl'

import {
  priceRegisterConfig,
  priceFieldProps,
  urlRegisterConfig,
} from './Fields/config'
import ConfirmedBooking from './Fields/ConfirmedBooking'
import Input from './Fields/RHFInput'
import TextArea from './Fields/RHFTextArea'
import StartEndAddressFields from './Fields/StartEndAddressFields'
import StartEndDateTimeFields from './Fields/StartEndDateTimeFields'
import { useDefaultDatePickerMonthYear } from './Hooks/useDefaultDates'
import type { CreateBusItemViewModel } from '../Create/models'
import type { PatchBusItemViewModel } from '../Edit/models'

import type { AutocompletePlaceParam } from 'tripPlanner/types/google'

const AUTOCOMPLETE_TYPES: AutocompletePlaceParam = [
  'bus_station',
  'transit_station',
  'train_station',
  'travel_agency',
  'route',
]

interface Props {
  tripId: string
}

function BusFields({ tripId }: Props) {
  const intl = useIntl()
  const methods = useFormContext<
    CreateBusItemViewModel | PatchBusItemViewModel
  >()
  const { control, watch, register } = methods
  const watchBooked = watch('isBooked')

  const { defaultMonth, defaultYear } = useDefaultDatePickerMonthYear({
    tripId,
  })

  const departureDateLabel = intl.formatMessage({
    id: 'bejuOsDWzO',
    defaultMessage: 'Departure date',
    description: 'Form label for departure date field in bus trip item editor',
  })

  const arrivalDateLabel = intl.formatMessage({
    id: 'IoXFWAA5hY',
    defaultMessage: 'Arrival date',
    description: 'Form label for arrival date field in bus trip item editor',
  })

  const operatorLabel = intl.formatMessage({
    id: 'KnbAeM9Q+3',
    defaultMessage: 'Operator',
    description: 'Form label for operator name field in bus trip item editor',
  })

  const websiteLabel = intl.formatMessage({
    id: 'qZNg9EJ9wq',
    defaultMessage: 'Website',
    description: 'Form label for website URL field in bus trip item editor',
  })

  const confirmationNumberLabel = intl.formatMessage({
    id: 't8Xcv7hJ9t',
    defaultMessage: 'Confirmation number',
    description: 'Form label for booking confirmation number in bus trip item editor',
  })

  const seatLabel = intl.formatMessage({
    id: 'Bm96XTkZWM',
    defaultMessage: 'Seat',
    description: 'Form label for seat field in bus trip item editor',
  })

  const seatPlaceholder = intl.formatMessage({
    id: 'PC3SKsZlJO',
    defaultMessage: 'Seat number',
    description: 'Placeholder text for seat number input in bus trip item editor',
  })

  const priceLabel = intl.formatMessage({
    id: 'JYxdN2ZMCh',
    defaultMessage: 'Price',
    description: 'Form label for price field in bus trip item editor',
  })

  const additionalNotesLabel = intl.formatMessage({
    id: 'DdAK3ppNZi',
    defaultMessage: 'Additional notes',
    description: 'Form label for additional notes field in bus trip item editor',
  })

  const notesPlaceholder = intl.formatMessage({
    id: 'iXC9gTlC65',
    defaultMessage: 'Add notes about this booking',
    description: 'Placeholder text for notes textarea in bus trip item editor',
  })

  return (
    <>
      <StartEndAddressFields
        autocompleteTypes={AUTOCOMPLETE_TYPES}
        tripId={tripId}
      />
      <StartEndDateTimeFields
        startDateLabel={departureDateLabel}
        endDateLabel={arrivalDateLabel}
        defaultMonth={defaultMonth}
        defaultYear={defaultYear}
      />

      <Input
        {...register('operatorName')}
        label={operatorLabel}
        placeholder={operatorLabel}
      />

      <Input
        {...register('url', { ...urlRegisterConfig })}
        label={websiteLabel}
        placeholder={websiteLabel}
      />

      <Controller
        name="isBooked"
        control={control}
        render={({ field }) => (
          <ConfirmedBooking
            toggled={!!field.value}
            onToggle={() => field.onChange(!field.value)}
          />
        )}
      />

      {watchBooked && (
        <>
          <Input
            {...register('confirmationCode')}
            label={confirmationNumberLabel}
            placeholder={confirmationNumberLabel}
          />
          <Input {...register('seat')} label={seatLabel} placeholder={seatPlaceholder} />
        </>
      )}

      <Input
        {...register('price', { ...priceRegisterConfig })}
        label={priceLabel}
        placeholder={priceLabel}
        {...priceFieldProps}
      />

      <TextArea
        {...register('notes')}
        placeholder={notesPlaceholder}
        label={additionalNotesLabel}
        resize="vertical"
      />
    </>
  )
}

export default BusFields
